import dayjs from '~/timeInit';

import { isValidDateTimeInput } from '../dateTime';
import { isKnownValue } from '../parseHelpers';
import { isSiteRegionCode } from '../regions';
import type { PayloadValidationResult } from '../types';
import { hasInternalRouteRegionPrefix, isHttpsUrl, isInternalRoute } from '../url';
import { validateRequiredString } from '../validationHelpers';

import { type OffersHubFieldLabelKey, fieldLabel } from './fieldLabels';
import { getOffersByTag, offerSectionLabel } from './offers';
import type {
  OffersHubHeroCarouselItem,
  OffersHubOffer,
  OffersHubOfferTag,
  OffersHubPayloadBuilderState,
} from './types';
import { OFFERS_HUB_OFFER_TAGS, OFFERS_HUB_URGENCY_TYPES } from './types';

export type OffersHubValidationResult = PayloadValidationResult;

const HERO_COPY_LIMITS: Partial<Record<OffersHubFieldLabelKey, number>> = {
  ctaLabel: 40,
  description: 130,
  label: 30,
  title: 70,
};

function validationLabel(sectionLabel: string, field: OffersHubFieldLabelKey): string {
  return `${sectionLabel} ${fieldLabel(field)}`;
}

function validateEligibleRegions(regions: Array<string>, label: string, errors: Array<string>) {
  const invalidRegions = regions.filter((region) => !isSiteRegionCode(region));
  if (invalidRegions.length > 0) {
    errors.push(`${validationLabel(label, 'eligibleRegions')} has invalid codes: ${invalidRegions.join(', ')}`);
  }
}

function validateTextLimit(
  value: string,
  label: string,
  field: OffersHubFieldLabelKey,
  limits: Partial<Record<OffersHubFieldLabelKey, number>>,
  errors: Array<string>,
) {
  const limit = limits[field];
  if (limit !== undefined && value.trim().length > limit) {
    errors.push(`${validationLabel(label, field)} must be ${limit} characters or fewer`);
  }
}

function validateRequiredCopy(
  value: string,
  label: string,
  field: OffersHubFieldLabelKey,
  limits: Partial<Record<OffersHubFieldLabelKey, number>>,
  errors: Array<string>,
) {
  validateRequiredString(value, validationLabel(label, field), errors);
  validateTextLimit(value, label, field, limits, errors);
}

function validateCtaUrl(value: string, label: string, errors: Array<string>) {
  const ctaUrlLabel = validationLabel(label, 'ctaUrl');
  validateRequiredString(value, ctaUrlLabel, errors);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return;
  }
  if (isHttpsUrl(trimmedValue)) {
    return;
  }
  if (!isInternalRoute(trimmedValue)) {
    errors.push(`${ctaUrlLabel} must be either an https:// URL or an internal route starting with /`);
    return;
  }
  if (hasInternalRouteRegionPrefix(trimmedValue)) {
    errors.push(`${ctaUrlLabel} must not include a region code prefix for internal routes`);
  }
}

function validateDateRange(startAt: string, endAt: string, label: string, errors: Array<string>) {
  const isStartAtValid = isValidDateTimeInput(startAt);
  const isEndAtValid = isValidDateTimeInput(endAt);

  if (!isStartAtValid) {
    errors.push(`${validationLabel(label, 'startAt')} must be a valid datetime`);
  }
  if (!isEndAtValid) {
    errors.push(`${validationLabel(label, 'endAt')} must be a valid datetime`);
  }
  if (!isStartAtValid || !isEndAtValid) {
    return;
  }

  if (dayjs(endAt).isBefore(dayjs(startAt))) {
    errors.push(`${validationLabel(label, 'endAt')} must be the same as or after ${fieldLabel('startAt')}`);
  }
}

function validateHeroCarouselItem(item: OffersHubHeroCarouselItem, errors: Array<string>) {
  const label = item.draftLabel;
  validateEligibleRegions(item.eligibleRegions, label, errors);
  validateDateRange(item.startAt, item.endAt, label, errors);
  validateRequiredCopy(item.label, label, 'label', HERO_COPY_LIMITS, errors);
  validateRequiredString(item.imageId, validationLabel(label, 'imageId'), errors);
  validateRequiredCopy(item.title, label, 'title', HERO_COPY_LIMITS, errors);
  validateRequiredCopy(item.description, label, 'description', HERO_COPY_LIMITS, errors);
  validateRequiredCopy(item.ctaLabel, label, 'ctaLabel', HERO_COPY_LIMITS, errors);
  validateCtaUrl(item.ctaUrl, label, errors);
}

function validateOffer(offer: OffersHubOffer, label: string, errors: Array<string>) {
  validateEligibleRegions(offer.eligibleRegions, label, errors);
  validateDateRange(offer.startAt, offer.endAt, label, errors);

  if (!isKnownValue(offer.offerTag, OFFERS_HUB_OFFER_TAGS)) {
    errors.push(`${validationLabel(label, 'offerTag')} must be partner_offer or luxury_escapes_offer`);
  }
  if (!isKnownValue(offer.urgencyType, OFFERS_HUB_URGENCY_TYPES)) {
    errors.push(`${validationLabel(label, 'urgencyType')} must be none, limited_time, or countdown`);
  }

  if (offer.offerTag === 'partner_offer') {
    validateRequiredString(offer.brandName, validationLabel(label, 'brandName'), errors);
    validateRequiredString(offer.industry, validationLabel(label, 'industry'), errors);
  }

  validateRequiredString(offer.imageId, validationLabel(label, 'imageId'), errors);
  validateRequiredString(offer.title, validationLabel(label, 'title'), errors);
  validateRequiredString(offer.description, validationLabel(label, 'description'), errors);
  validateRequiredString(offer.ctaLabel, validationLabel(label, 'ctaLabel'), errors);
  validateCtaUrl(offer.ctaUrl, label, errors);
}

function offerValidationLabel(offer: OffersHubOffer): string {
  return `${offerSectionLabel(offer.offerTag)} ${offer.draftLabel.replace(/^Offer\s+/, '')}`;
}

function validateOffersByTag(state: OffersHubPayloadBuilderState, offerTag: OffersHubOfferTag, errors: Array<string>) {
  getOffersByTag(state.offers, offerTag).forEach((offer) => {
    validateOffer(offer, offerValidationLabel(offer), errors);
  });
}

export function validateOffersHubPayload(state: OffersHubPayloadBuilderState): OffersHubValidationResult {
  const errors: Array<string> = [];

  state.heroCarouselItems.forEach((item) => validateHeroCarouselItem(item, errors));
  OFFERS_HUB_OFFER_TAGS.forEach((offerTag) => validateOffersByTag(state, offerTag, errors));

  return {
    isValid: errors.length === 0,
    errors,
  };
}
